import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Layout,
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Modal,
  List,
  Popconfirm,
  Alert,
  Divider,
  Spin,
  Empty,
  Image,
  Popover,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import request from '@/utils/request';
import { formatCents } from '@/utils/format';
import { thumb } from '@/utils/image';
import type {
  BundleDetailDTO,
  BundleInstructionDTO,
  BundleExampleDTO,
  ImageModelDTO,
} from '@zero/shared';
import ImagePickerField from '@/components/ImagePicker/ImagePickerField';

const { Sider, Content } = Layout;

interface ExperimentRow {
  id: number;
  name: string;
}

interface RecordRow {
  id: number;
  modelSlug: string;
  imageModelId: number | null;
  prompt: string | null;
  inputsJson: Record<string, unknown>;
  referenceResourceIds: number[];
  userInputResourceIds: number[];
  outputImageIds: number[];
  costUsd: number | null;
  createdAt: string;
}

interface ResourceRow {
  id: number;
  imageId: number;
  label: string | null;
  kind: string;
  image: { id: number; accessUrl: string };
}

interface PickedImage {
  record: RecordRow;
  imageId: number;
  experimentName: string;
  referenceImageIds: number[];
}

interface InstructionDraft extends BundleInstructionDTO {
  localId: string;
}

interface ExampleDraft extends BundleExampleDTO {
  localId: string;
}

let localIdCounter = 0;
const newLocalId = () => `l${++localIdCounter}-${Date.now()}`;

const BundleBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const bundleId = Number(id);
  const [bundle, setBundle] = useState<BundleDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState<ImageModelDTO[]>([]);
  const [instructions, setInstructions] = useState<InstructionDraft[]>([]);
  const [examples, setExamples] = useState<ExampleDraft[]>([]);
  const [form] = Form.useForm();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [imageUrlMap, setImageUrlMap] = useState<Map<number, string>>(new Map());

  const fetchImageUrls = useCallback(async (ids: number[]) => {
    const missing: number[] = [];
    for (const id of ids) {
      if (!imageUrlMap.has(id)) missing.push(id);
    }
    if (missing.length === 0) return;
    const unique = Array.from(new Set(missing));
    try {
      const res = await request.get<{ items: { id: number; accessUrl: string }[] }>(
        `/creator/images?ids=${unique.join(',')}`,
      );
      setImageUrlMap((prev) => {
        const next = new Map(prev);
        for (const im of (res.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          next.set(im.id, im.accessUrl);
        }
        return next;
      });
    } catch {
      /* noop */
    }
  }, [imageUrlMap]);

  const load = async () => {
    setLoading(true);
    try {
      const [detailRes, modelsRes] = await Promise.all([
        request.get<BundleDetailDTO>(`/creator/bundles/${bundleId}`),
        request.get<ImageModelDTO[]>('/creator/image-models'),
      ]);
      const detail = detailRes.data as unknown as BundleDetailDTO;
      setBundle(detail);
      setModels(modelsRes.data as unknown as ImageModelDTO[]);
      setInstructions(detail.instructions.map((i) => ({ ...i, localId: newLocalId() })));
      setExamples(detail.examples.map((e) => ({ ...e, localId: newLocalId() })));
      form.setFieldsValue({
        title: detail.title,
        description: detail.description,
        uploadInstruction: detail.uploadInstruction,
        requiredUploadCount: detail.requiredUploadCount,
        maxUploadCount: detail.maxUploadCount,
      });
      // Pre-fetch image URLs for instruction list display.
      const needed = new Set<number>();
      for (const ins of detail.instructions) {
        for (const id of ins.referenceImageIds) needed.add(id);
        if (ins.sampleOutputImageId) needed.add(ins.sampleOutputImageId);
      }
      if (needed.size > 0) {
        const res = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${Array.from(needed).join(',')}`,
        );
        const map = new Map<number, string>();
        for (const im of (res.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          map.set(im.id, im.accessUrl);
        }
        setImageUrlMap(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [bundleId]);

  /** Notify the creator the first time an edit auto-takes a published bundle
   *  offline — easy to miss the small status tag changing otherwise. */
  const notifyAutoOfflineIfNeeded = (
    prevStatus: string | undefined,
    nextStatus: string | undefined,
  ) => {
    if (prevStatus === 'published' && nextStatus === 'offline') {
      message.info('已自动下架，编辑完成后点右下「重新上架」即可恢复');
    }
  };

  const scheduleSave = (values: Record<string, unknown>) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await request.patch<BundleDetailDTO>(`/creator/bundles/${bundleId}`, values);
        const next = res.data as unknown as BundleDetailDTO;
        notifyAutoOfflineIfNeeded(bundle?.status, next.status);
        setBundle(next);
      } catch (e) {
        if (e instanceof Error) message.error(e.message);
      } finally {
        setSaving(false);
      }
    }, 500);
  };

  const patchField = async (patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      const res = await request.patch<BundleDetailDTO>(`/creator/bundles/${bundleId}`, patch);
      const next = res.data as unknown as BundleDetailDTO;
      notifyAutoOfflineIfNeeded(bundle?.status, next.status);
      setBundle(next);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveInstructions = async (next: InstructionDraft[]) => {
    setSaving(true);
    try {
      const res = await request.put<BundleDetailDTO>(`/creator/bundles/${bundleId}/instructions`, {
        instructions: next.map((i) => ({
          title: i.title,
          modelSlug: i.modelSlug,
          paramsJson: i.paramsJson,
          promptText: i.promptText,
          referenceImageIds: i.referenceImageIds,
          sampleOutputImageId: i.sampleOutputImageId,
          sourceRecordId: i.sourceRecordId,
          consumerUploadSlot: i.consumerUploadSlot,
        })),
      });
      const next2 = res.data as unknown as BundleDetailDTO;
      notifyAutoOfflineIfNeeded(bundle?.status, next2.status);
      setBundle(next2);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveExamples = async (next: ExampleDraft[]) => {
    setSaving(true);
    try {
      const res = await request.put<BundleDetailDTO>(`/creator/bundles/${bundleId}/examples`, {
        examples: next.map((e) => ({
          beforeImageId: e.beforeImageId,
          afterImageId: e.afterImageId,
          caption: e.caption,
        })),
      });
      const next2 = res.data as unknown as BundleDetailDTO;
      notifyAutoOfflineIfNeeded(bundle?.status, next2.status);
      setBundle(next2);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const moveInstruction = (index: number, dir: -1 | 1) => {
    const next = [...instructions];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setInstructions(next);
    saveInstructions(next);
  };

  const removeInstruction = (index: number) => {
    const next = instructions.filter((_, i) => i !== index);
    setInstructions(next);
    saveInstructions(next);
  };

  const updateInstructionTitle = (index: number, title: string) => {
    const next = [...instructions];
    next[index] = { ...next[index], title };
    setInstructions(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveInstructions(next), 500);
  };

  /** Batch-add: each picked imageId becomes one instruction, title = experiment name. */
  const addFromPicker = (picks: PickedImage[]) => {
    const drafts: InstructionDraft[] = picks.map((p) => {
      const model = models.find((m) => m.slug === p.record.modelSlug);
      return {
        localId: newLocalId(),
        id: 0,
        orderIndex: instructions.length, // will be reindexed server-side
        title: p.experimentName || p.record.modelSlug,
        modelSlug: p.record.modelSlug,
        imageModelId: p.record.imageModelId ?? model?.id ?? null,
        paramsJson: p.record.inputsJson,
        promptText: p.record.prompt,
        referenceImageIds: p.referenceImageIds,
        sampleOutputImageId: p.imageId,
        sourceRecordId: p.record.id,
        consumerUploadSlot: 0,
      };
    });
    const next = [...instructions, ...drafts];
    setInstructions(next);
    saveInstructions(next);
    // Pre-fetch URLs for newly added image ids so they render immediately.
    const newIds: number[] = [];
    for (const p of picks) {
      newIds.push(p.imageId);
      for (const r of p.referenceImageIds) newIds.push(r);
    }
    if (newIds.length > 0) fetchImageUrls(newIds);
    message.success(`已添加 ${drafts.length} 条指令`);
  };

  const submitForReview = async () => {
    try {
      setSaving(true);
      await request.post(`/creator/bundles/${bundleId}/submit-review`);
      message.success('已提交审核');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const takeOffline = async () => {
    try {
      await request.post(`/creator/bundles/${bundleId}/take-offline`);
      message.success('已下架');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  /** offline → published, no review. Used by creators to re-list a bundle
   *  they took offline (manually or implicitly, by editing a published one). */
  const republish = async () => {
    try {
      setSaving(true);
      await request.post(`/creator/bundles/${bundleId}/republish`);
      message.success('已上架');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = useMemo(() => {
    if (!bundle) return null;
    const map: Record<string, { text: string; color: string }> = {
      draft: { text: '草稿', color: 'default' },
      pending_review: { text: '审核中', color: 'processing' },
      published: { text: '已发布', color: 'success' },
      rejected: { text: '已驳回', color: 'error' },
      offline: { text: '已下架', color: 'warning' },
    };
    const l = map[bundle.status] ?? { text: bundle.status, color: 'default' };
    return <Tag color={l.color}>{l.text}</Tag>;
  }, [bundle]);

  if (loading || !bundle) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin />
      </div>
    );
  }

  // Offline bundles ARE editable now — creators take them offline (explicitly
  // or by editing a published bundle, which auto-flips to offline) and then
  // re-publish via 重新上架 when ready. Only pending_review still locks
  // editing, since admin is currently reviewing.
  const canEdit = bundle.status !== 'pending_review';

  return (
    <Layout style={{ background: 'transparent', height: 'calc(100vh - 112px)' }}>
      <Sider width={440} theme="light" style={{ background: 'transparent' }}>
        <div className="wb-column">
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/bundles')}>
              返回
            </Button>
            {statusLabel}
          </Space>

          {bundle.status === 'rejected' && bundle.rejectionReason && (
            <Alert type="error" message="审核驳回" description={bundle.rejectionReason} showIcon />
          )}
          {bundle.status === 'offline' && (
            <Alert
              type="warning"
              showIcon
              message="套图已下架"
              description="编辑已发布的套图会自动下架。修改完成后点右下「重新上架」立即重新发布（无需再审核）。"
            />
          )}

          <Form
            form={form}
            layout="vertical"
            disabled={!canEdit}
            onValuesChange={(_changed, all) => scheduleSave(all)}
          >
            <Form.Item name="title" label="标题">
              <Input />
            </Form.Item>
            <Form.Item name="description" label="详细介绍">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="uploadInstruction" label="用户上传要求（提示文案）">
              <Input.TextArea rows={2} placeholder="例如：请上传 2 张清晰正脸照" />
            </Form.Item>
            <Space size="middle">
              <Form.Item name="requiredUploadCount" label="最少上传数" style={{ width: 140 }}>
                <InputNumber min={1} max={9} />
              </Form.Item>
              <Form.Item name="maxUploadCount" label="最多上传数" style={{ width: 140 }}>
                <InputNumber min={1} max={9} />
              </Form.Item>
            </Space>
          </Form>

          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>
              封面
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              <ImagePickerField
                value={bundle.coverImageId}
                onChange={(imgId) => patchField({ coverImageId: imgId })}
                disabled={!canEdit}
                placeholder="从图片库选择封面"
                aspectRatio={1.4}
              />
            </div>
          </div>

          <div>
            <Typography.Text strong style={{ fontSize: 13 }}>
              示例原图（展示给消费者看的"你要上传的样子"）
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              <ImagePickerField
                value={bundle.sampleOriginalImageId}
                onChange={(imgId) => patchField({ sampleOriginalImageId: imgId })}
                disabled={!canEdit}
                placeholder="选择示例原图"
                height={140}
              />
            </div>
          </div>

          <Card size="small" title="成本预估">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Typography.Text>
                fal 总成本:{' '}
                <Typography.Text strong>
                  ${bundle.totalFalCostUsd?.toFixed(4) ?? '—'}
                </Typography.Text>
              </Typography.Text>
              <Typography.Text>
                定价:{' '}
                <Typography.Text strong style={{ color: '#D97706' }}>
                  {formatCents(bundle.priceRmbCents ?? 0)}
                </Typography.Text>{' '}
                / {bundle.priceCoins ?? 0} 币
              </Typography.Text>
              <Typography.Text>
                每单创作者分成:{' '}
                <Typography.Text strong style={{ color: '#15803D' }}>
                  {formatCents(bundle.creatorShareCents ?? 0)}
                </Typography.Text>
              </Typography.Text>
            </Space>
          </Card>

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {saving ? '保存中…' : '已自动保存'}
            </Typography.Text>
            <Space>
              {bundle.status === 'published' && (
                <Popconfirm title="确定下架？" onConfirm={takeOffline}>
                  <Button>下架</Button>
                </Popconfirm>
              )}
              {bundle.status === 'offline' && (
                <Button type="primary" onClick={republish}>
                  重新上架
                </Button>
              )}
              {(bundle.status === 'draft' || bundle.status === 'rejected') && (
                <Button type="primary" onClick={submitForReview}>
                  提交审核
                </Button>
              )}
            </Space>
          </Space>
        </div>
      </Sider>

      <Content style={{ marginLeft: 16, background: 'transparent', minHeight: 0 }}>
        <div className="wb-column">
          <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 16 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              指令列表（{instructions.length}）
            </Typography.Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canEdit}
              onClick={() => setPickerOpen(true)}
            >
              从实验台挑选图片
            </Button>
          </Space>

          {instructions.length === 0 ? (
            <Empty
              className="brand-empty"
              description="点右上角『从实验台挑选图片』，每张图会各自成为一条指令"
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {instructions.map((ins, index) => (
                <InstructionCard
                  key={ins.localId}
                  ins={ins}
                  index={index}
                  total={instructions.length}
                  canEdit={canEdit}
                  urlMap={imageUrlMap}
                  onMoveUp={() => moveInstruction(index, -1)}
                  onMoveDown={() => moveInstruction(index, 1)}
                  onDelete={() => removeInstruction(index)}
                  onTitleChange={(t) => updateInstructionTitle(index, t)}
                />
              ))}
            </div>
          )}

          <Divider />

          <ExamplesSection
            examples={examples}
            setExamples={setExamples}
            saveExamples={saveExamples}
            canEdit={canEdit}
          />
        </div>
      </Content>

      <ImageMultiPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={addFromPicker}
      />
    </Layout>
  );
};

const THUMB = 60;

const ParamsPopover: React.FC<{ modelSlug: string; params: Record<string, unknown> }> = ({
  modelSlug,
  params,
}) => (
  <div style={{ maxWidth: 260 }}>
    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>模型</div>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>{modelSlug}</div>
    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>参数</div>
    {Object.keys(params).length === 0 ? (
      <div style={{ color: '#94A3B8', fontSize: 12 }}>（默认）</div>
    ) : (
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        {Object.entries(params).map(([k, v]) => (
          <div key={k}>
            <span style={{ color: '#94A3B8' }}>{k}:</span>{' '}
            <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const EmptyThumb: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      width: THUMB,
      height: THUMB,
      border: '1px dashed #E7E2D6',
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#94A3B8',
      fontSize: 10,
      flexShrink: 0,
    }}
  >
    {text}
  </div>
);

const InstructionCard: React.FC<{
  ins: InstructionDraft;
  index: number;
  total: number;
  canEdit: boolean;
  urlMap: Map<number, string>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onTitleChange: (t: string) => void;
}> = ({ ins, index, total, canEdit, urlMap, onMoveUp, onMoveDown, onDelete, onTitleChange }) => {
  const refIds = ins.referenceImageIds.slice(0, 3);
  const refOverflow = Math.max(0, ins.referenceImageIds.length - 3);
  const outUrl = ins.sampleOutputImageId ? urlMap.get(ins.sampleOutputImageId) : undefined;

  return (
    <div
      style={{
        background: '#FDFCF9',
        padding: 10,
        borderRadius: 10,
        border: '1px solid #F0EBDE',
      }}
    >
      {/* Header: title input + model + params popover + actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          #{index + 1}
        </Typography.Text>
        <Input
          size="small"
          value={ins.title}
          disabled={!canEdit}
          onChange={(e) => onTitleChange(e.target.value)}
          style={{ flex: '1 1 200px', maxWidth: 320 }}
        />
        <Tag color="purple" style={{ margin: 0 }}>
          {ins.modelSlug}
        </Tag>
        <Popover
          content={<ParamsPopover modelSlug={ins.modelSlug} params={ins.paramsJson} />}
          placement="topRight"
          mouseEnterDelay={0.3}
        >
          <Button size="small" type="text" style={{ color: '#94A3B8' }}>
            参数 {Object.keys(ins.paramsJson).length}
          </Button>
        </Popover>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
          <Button
            size="small"
            type="text"
            icon={<ArrowUpOutlined />}
            disabled={!canEdit || index === 0}
            onClick={onMoveUp}
          />
          <Button
            size="small"
            type="text"
            icon={<ArrowDownOutlined />}
            disabled={!canEdit || index === total - 1}
            onClick={onMoveDown}
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!canEdit}
            onClick={onDelete}
          />
        </div>
      </div>

      {/* Row: reference images | prompt | sample output */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {ins.referenceImageIds.length === 0 ? (
            <EmptyThumb text="无参考" />
          ) : (
            <Image.PreviewGroup>
              {refIds.map((id) => {
                const url = urlMap.get(id);
                return (
                  <div
                    key={id}
                    style={{
                      width: THUMB,
                      height: THUMB,
                      borderRadius: 6,
                      overflow: 'hidden',
                      border: '1px solid #EEE7D4',
                      flexShrink: 0,
                      background: '#F1F5F9',
                    }}
                  >
                    {url ? (
                      <Image
                        src={thumb(url, 120)}
                        preview={{ src: url, mask: null }}
                        width={THUMB}
                        height={THUMB}
                        style={{ objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94A3B8',
                          fontSize: 10,
                        }}
                      >
                        #{id}
                      </div>
                    )}
                  </div>
                );
              })}
              {refOverflow > 0 && (
                <div
                  style={{
                    width: THUMB,
                    height: THUMB,
                    borderRadius: 6,
                    background: '#F8F5EC',
                    border: '1px solid #EEE7D4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748B',
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  +{refOverflow}
                </div>
              )}
            </Image.PreviewGroup>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: '#334155',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 4,
            overflow: 'hidden',
            alignSelf: 'center',
          }}
        >
          {ins.promptText ? (
            ins.promptText
          ) : (
            <span style={{ color: '#94A3B8' }}>(无指令)</span>
          )}
        </div>

        <div style={{ flexShrink: 0 }}>
          {ins.sampleOutputImageId ? (
            <div
              style={{
                width: THUMB,
                height: THUMB,
                borderRadius: 6,
                overflow: 'hidden',
                border: '1px solid #EEE7D4',
                background: '#F1F5F9',
              }}
            >
              {outUrl ? (
                <Image
                  src={thumb(outUrl, 120)}
                  preview={{ src: outUrl, mask: null }}
                  width={THUMB}
                  height={THUMB}
                  style={{ objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94A3B8',
                    fontSize: 10,
                  }}
                >
                  #{ins.sampleOutputImageId}
                </div>
              )}
            </div>
          ) : (
            <EmptyThumb text="无样图" />
          )}
        </div>
      </div>
    </div>
  );
};

const ExamplesSection: React.FC<{
  examples: ExampleDraft[];
  setExamples: (e: ExampleDraft[]) => void;
  saveExamples: (e: ExampleDraft[]) => void;
  canEdit: boolean;
}> = ({ examples, setExamples, saveExamples, canEdit }) => {
  const addEmpty = () => {
    const next: ExampleDraft[] = [
      ...examples,
      {
        localId: newLocalId(),
        id: 0,
        orderIndex: examples.length,
        beforeImageId: null,
        afterImageId: null,
        caption: null,
      },
    ];
    setExamples(next);
    saveExamples(next);
  };

  const update = (index: number, patch: Partial<ExampleDraft>) => {
    const next = examples.map((e, i) => (i === index ? { ...e, ...patch } : e));
    setExamples(next);
    saveExamples(next);
  };

  const remove = (index: number) => {
    const next = examples.filter((_, i) => i !== index);
    setExamples(next);
    saveExamples(next);
  };

  return (
    <div>
      <Space style={{ justifyContent: 'space-between', width: '100%', marginBottom: 12 }}>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            样例对比（{examples.length}）
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            展示给消费者的"前 / 后"对比，可编辑对照说明
          </Typography.Text>
        </div>
        <Button
          size="small"
          icon={<PlusOutlined />}
          disabled={!canEdit}
          onClick={addEmpty}
        >
          添加对比组
        </Button>
      </Space>
      {examples.length === 0 ? (
        <Empty
          className="brand-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无样例对比"
          style={{ padding: '20px 0' }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {examples.map((ex, index) => (
            <div
              key={ex.localId}
              style={{
                background: '#FDFCF9',
                padding: 12,
                borderRadius: 10,
                border: '1px solid #F0EBDE',
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 130 }}>
                  <Typography.Text style={{ fontSize: 11, color: '#64748B' }}>前</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <ImagePickerField
                      value={ex.beforeImageId}
                      onChange={(v) => update(index, { beforeImageId: v })}
                      disabled={!canEdit}
                      placeholder="原图"
                      height={120}
                    />
                  </div>
                </div>
                <div style={{ width: 130 }}>
                  <Typography.Text style={{ fontSize: 11, color: '#64748B' }}>后</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <ImagePickerField
                      value={ex.afterImageId}
                      onChange={(v) => update(index, { afterImageId: v })}
                      disabled={!canEdit}
                      placeholder="效果图"
                      height={120}
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <Typography.Text style={{ fontSize: 11, color: '#64748B' }}>标题 / 说明</Typography.Text>
                  <Input
                    style={{ marginTop: 4 }}
                    placeholder="如：普通 → 油画"
                    value={ex.caption ?? ''}
                    onChange={(e) => update(index, { caption: e.target.value })}
                    disabled={!canEdit}
                  />
                  {canEdit && (
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      style={{ marginTop: 8 }}
                      onClick={() => remove(index)}
                    >
                      删除该对比组
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ImageMultiPickerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onPick: (picks: PickedImage[]) => void;
}> = ({ open, onClose, onPick }) => {
  const [experiments, setExperiments] = useState<ExperimentRow[]>([]);
  const [picked, setPicked] = useState<ExperimentRow | null>(null);
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [urlMap, setUrlMap] = useState<Map<number, string>>(new Map());
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Reset state whenever the modal closes so reopening starts clean.
  useEffect(() => {
    if (open) return;
    setPicked(null);
    setRecords([]);
    setUrlMap(new Map());
    setSelected(new Set());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [expRes, resRes] = await Promise.all([
        request.get<{ items: ExperimentRow[] }>('/creator/experiments?pageSize=100'),
        request.get<{ items: ResourceRow[] }>('/creator/resources?pageSize=500'),
      ]);
      setExperiments((expRes.data as unknown as { items: ExperimentRow[] }).items);
      setResources((resRes.data as unknown as { items: ResourceRow[] }).items);
    })();
  }, [open]);

  useEffect(() => {
    if (!picked) return;
    let alive = true;
    (async () => {
      setLoadingRecords(true);
      try {
        const res = await request.get<{ items: RecordRow[] }>(
          `/creator/experiments/${picked.id}/records?pageSize=200`,
        );
        if (!alive) return;
        const list = (res.data as unknown as { items: RecordRow[] }).items.filter(
          (r) => r.outputImageIds.length > 0,
        );
        setRecords(list);
        const ids = list.flatMap((r) => r.outputImageIds);
        if (ids.length > 0) {
          const imgRes = await request.get<{ items: { id: number; accessUrl: string }[] }>(
            `/creator/images?ids=${Array.from(new Set(ids)).join(',')}`,
          );
          if (!alive) return;
          const map = new Map<number, string>();
          for (const im of (imgRes.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
            map.set(im.id, im.accessUrl);
          }
          setUrlMap(map);
        }
      } finally {
        if (alive) setLoadingRecords(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [picked]);

  const resourceMap = useMemo(() => {
    const m = new Map<number, ResourceRow>();
    for (const r of resources) m.set(r.id, r);
    return m;
  }, [resources]);

  const toggle = useCallback((imageId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  }, []);

  const submit = () => {
    if (!picked) return;
    const picks: PickedImage[] = [];
    for (const r of records) {
      for (const imgId of r.outputImageIds) {
        if (!selected.has(imgId)) continue;
        // Only true reference images travel into BundleInstruction.referenceImageIds.
        // The creator's own user-input images were just for testing; consumers will
        // upload their own when running the bundle. Mixing user-input here would
        // get sandwiched between [creator's-user-input, consumer's-image] at run
        // time and corrupt outputs.
        const refIds = r.referenceResourceIds
          .map((rid) => resourceMap.get(rid)?.imageId)
          .filter((v): v is number => typeof v === 'number');
        picks.push({
          record: r,
          imageId: imgId,
          experimentName: picked.name,
          referenceImageIds: refIds,
        });
      }
    }
    onPick(picks);
    onClose();
  };

  return (
    <Modal
      title="从实验台挑选图片 → 每张图 = 一条指令"
      open={open}
      width={860}
      onCancel={onClose}
      maskClosable={false}
      footer={
        picked ? (
          <Space>
            <Typography.Text type="secondary">已选 {selected.size} 张</Typography.Text>
            <Button
              onClick={() => {
                setPicked(null);
                setRecords([]);
                setUrlMap(new Map());
                setSelected(new Set());
              }}
            >
              返回实验列表
            </Button>
            <Button type="primary" disabled={selected.size === 0} onClick={submit}>
              加入 {selected.size} 条指令
            </Button>
          </Space>
        ) : null
      }
    >
      {!picked ? (
        <List
          dataSource={experiments}
          locale={{ emptyText: '还没有实验，先去实验台创作吧' }}
          renderItem={(exp) => (
            <List.Item
              style={{ cursor: 'pointer', padding: '10px 12px', borderRadius: 8 }}
              onClick={() => setPicked(exp)}
              actions={[<a key="pick">选图 →</a>]}
            >
              <List.Item.Meta title={<Typography.Text strong>{exp.name}</Typography.Text>} />
            </List.Item>
          )}
        />
      ) : loadingRecords ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : records.length === 0 ? (
        <Empty className="brand-empty" description="该实验还没有成功输出" style={{ padding: 40 }} />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            maxHeight: 520,
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {records.map((r) => (
            <PickerRecordCard
              key={r.id}
              row={r}
              resourceMap={resourceMap}
              urlMap={urlMap}
              selected={selected}
              onToggle={toggle}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

const PickerRecordCard: React.FC<{
  row: RecordRow;
  resourceMap: Map<number, ResourceRow>;
  urlMap: Map<number, string>;
  selected: Set<number>;
  onToggle: (imageId: number) => void;
}> = ({ row, resourceMap, urlMap, selected, onToggle }) => {
  const inputResources = [...row.referenceResourceIds, ...row.userInputResourceIds]
    .map((id) => resourceMap.get(id))
    .filter(Boolean) as ResourceRow[];

  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: 8,
        background: '#fff',
        border: '1px solid #F0EBDE',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          fontSize: 12,
          flexWrap: 'wrap',
        }}
      >
        <Tag color="purple" style={{ margin: 0 }}>
          {row.modelSlug}
        </Tag>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          参数 {Object.keys(row.inputsJson).length} 项
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {inputResources.length === 0 ? (
            <EmptyThumb text="无输入" />
          ) : (
            <Image.PreviewGroup>
              {inputResources.slice(0, 3).map((r) => (
                <div
                  key={r.id}
                  style={{
                    width: THUMB,
                    height: THUMB,
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: '1px solid #EEE7D4',
                    flexShrink: 0,
                  }}
                >
                  <Image
                    src={thumb(r.image.accessUrl, 120)}
                    preview={{ src: r.image.accessUrl, mask: null }}
                    width={THUMB}
                    height={THUMB}
                    style={{ objectFit: 'cover', display: 'block' }}
                  />
                </div>
              ))}
              {inputResources.length > 3 && (
                <div
                  style={{
                    width: THUMB,
                    height: THUMB,
                    borderRadius: 6,
                    background: '#F8F5EC',
                    border: '1px solid #EEE7D4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748B',
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  +{inputResources.length - 3}
                </div>
              )}
            </Image.PreviewGroup>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: '#334155',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 4,
            overflow: 'hidden',
            alignSelf: 'center',
          }}
        >
          {row.prompt ? row.prompt : <span style={{ color: '#94A3B8' }}>(无指令)</span>}
        </div>

        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {row.outputImageIds.map((imgId) => {
            const isSelected = selected.has(imgId);
            const url = urlMap.get(imgId);
            return (
              <div
                key={imgId}
                role="button"
                onClick={() => onToggle(imgId)}
                style={{
                  position: 'relative',
                  width: THUMB,
                  height: THUMB,
                  borderRadius: 6,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: isSelected ? '2px solid #D97706' : '1px solid #EEE7D4',
                  boxShadow: isSelected ? '0 0 0 2px rgba(217,119,6,.18)' : undefined,
                  background: '#F1F5F9',
                  flexShrink: 0,
                }}
              >
                {url ? (
                  <img
                    src={thumb(url, 120)}
                    alt=""
                    draggable={false}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      pointerEvents: 'none',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94A3B8',
                      fontSize: 10,
                    }}
                  >
                    #{imgId}
                  </div>
                )}
                {isSelected && (
                  <CheckCircleFilled
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      fontSize: 16,
                      color: '#D97706',
                      background: '#fff',
                      borderRadius: '50%',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BundleBuilderPage;
